import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Plus, Upload, RefreshCw, X, Check, Pencil,
  CircleDot, Calendar, User, Bold, Italic, List, Heading,
} from 'lucide-react';
import { T, springs } from '../lib/tokens';
import { supabase } from '../lib/supabase';
import { Glass } from '../components/glass/Glass';
import { FounderCard } from '../components/cards/FounderCard';
import { MeetingCard } from '../components/cards/MeetingCard';
import { isLocalAvailable, apiFetch } from '../lib/api';

// ─── Shared input style ─────────────────────────────────────────────────────
const inputStyle = {
  width: '100%', padding: '10px 14px', borderRadius: 10, fontSize: 13,
  border: '1px solid rgba(0,0,0,0.10)', background: 'rgba(255,255,255,0.8)',
  fontFamily: 'inherit', color: T.text, outline: 'none',
};

// ─── Inline Editable Field ──────────────────────────────────────────────────
const InlineEdit = ({ value, onSave, placeholder, multiline, style: extraStyle }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const ref = useRef(null);

  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);

  const save = () => {
    setEditing(false);
    if (draft.trim() !== (value || '').trim()) onSave(draft.trim());
  };

  if (!editing) {
    return (
      <div
        onClick={() => { setDraft(value || ''); setEditing(true); }}
        style={{
          cursor: 'pointer', minHeight: 20, borderRadius: 6,
          padding: '2px 4px', margin: '-2px -4px',
          ...extraStyle,
        }}
        title="Click to edit"
      >
        {value || <span style={{ color: T.textLight, fontStyle: 'italic' }}>{placeholder || 'Click to edit'}</span>}
      </div>
    );
  }

  if (multiline) {
    return (
      <textarea
        ref={ref}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); } }}
        style={{ ...inputStyle, minHeight: 80, resize: 'vertical', ...extraStyle }}
        placeholder={placeholder}
      />
    );
  }

  return (
    <input
      ref={ref}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={save}
      onKeyDown={e => {
        if (e.key === 'Enter') save();
        if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
      }}
      style={{ ...inputStyle, ...extraStyle }}
      placeholder={placeholder}
    />
  );
};

// ─── Rich Text Notes Editor ─────────────────────────────────────────────────
const NotesEditor = ({ value, onSave }) => {
  const editorRef = useRef(null);
  const saveTimer = useRef(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const debouncedSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      if (editorRef.current) onSave(editorRef.current.innerHTML);
    }, 1000);
  }, [onSave]);

  const exec = (cmd, val) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    debouncedSave();
  };

  const toolBtn = (icon, cmd, label) => (
    <button
      key={cmd}
      onMouseDown={e => { e.preventDefault(); exec(cmd); }}
      title={label}
      style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: T.textMuted, opacity: 0.7,
      }}
    >
      {React.createElement(icon, { size: 13 })}
    </button>
  );

  return (
    <div style={{
      border: focused ? `1px solid ${T.blue}` : '1px solid rgba(0,0,0,0.08)',
      borderRadius: 12, overflow: 'hidden', transition: 'border-color 0.15s',
    }}>
      <div style={{
        display: 'flex', gap: 2, padding: '6px 10px',
        borderBottom: '1px solid rgba(0,0,0,0.06)', background: 'rgba(0,0,0,0.02)',
      }}>
        {toolBtn(Bold, 'bold', 'Bold')}
        {toolBtn(Italic, 'italic', 'Italic')}
        {toolBtn(List, 'insertUnorderedList', 'Bullet list')}
        {toolBtn(Heading, 'formatBlock', 'Heading')}
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={debouncedSave}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          if (editorRef.current) onSave(editorRef.current.innerHTML);
        }}
        style={{
          minHeight: 120, padding: '12px 14px', fontSize: 13, lineHeight: 1.6,
          color: T.text, fontFamily: 'inherit', outline: 'none',
          background: 'rgba(255,255,255,0.6)',
        }}
        data-placeholder="Add notes about this founder..."
      />
    </div>
  );
};

// ─── Add/Edit Founder Dialog ────────────────────────────────────────────────
const FounderDialog = ({ founder, onClose, onUpdate }) => {
  const [name, setName] = useState(founder?.name || '');
  const [email, setEmail] = useState(founder?.email || '');
  const [company, setCompany] = useState(founder?.company_name || '');
  const [cohort, setCohort] = useState(founder?.cohort || '');
  const [saving, setSaving] = useState(false);
  const isEdit = !!founder;

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    if (isEdit) {
      await supabase.from('founders').update({
        name: name.trim(),
        email: email.trim() || null,
        company_name: company.trim() || null,
        cohort: cohort.trim() || null,
      }).eq('id', founder.id);
    } else {
      await supabase.from('founders').insert({
        name: name.trim(),
        email: email.trim() || null,
        company_name: company.trim() || null,
        cohort: cohort.trim() || null,
      });
    }
    setSaving(false);
    onUpdate();
    onClose();
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', zIndex: 100 }} />
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.97 }}
        transition={springs.gentle}
        style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 420, zIndex: 101,
        }}
      >
        <Glass depth={3} style={{ padding: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{isEdit ? 'Edit Founder' : 'Add Founder'}</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
              <X size={16} color={T.textMuted} />
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input style={inputStyle} placeholder="Name *" value={name} onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') save(); }} autoFocus />
            <input style={inputStyle} placeholder="Email" value={email} onChange={e => setEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') save(); }} />
            <input style={inputStyle} placeholder="Company" value={company} onChange={e => setCompany(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') save(); }} />
            <input style={inputStyle} placeholder="Cohort (e.g. 2026-Spring)" value={cohort} onChange={e => setCohort(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') save(); }} />
            <button onClick={save} disabled={!name.trim() || saving} style={{
              padding: '10px 20px', borderRadius: 10, border: 'none',
              background: T.blue, color: '#fff', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              opacity: !name.trim() || saving ? 0.5 : 1, marginTop: 4,
            }}>
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Founder'}
            </button>
          </div>
        </Glass>
      </motion.div>
    </>
  );
};

// ─── Import Meeting Dialog ───────────────────────────────────────────────────
const ImportMeetingDialog = ({ founder, onClose, onUpdate }) => {
  const [transcriptUrl, setTranscriptUrl] = useState('');
  const [transcriptText, setTranscriptText] = useState('');
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!transcriptUrl.trim() && !transcriptText.trim()) return;
    if (!isLocalAvailable()) { console.warn('Meeting import requires local server'); return; }
    setSaving(true);
    try {
      const res = await apiFetch('/meetings/import', {
        method: 'POST',
        body: JSON.stringify({
          founder_id: founder.id,
          transcript_url: transcriptUrl.trim() || null,
          transcript_text: transcriptText.trim() || null,
          meeting_date: meetingDate,
        }),
      });
      if (!res.ok) throw new Error('Import failed');
      onUpdate();
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', zIndex: 100 }} />
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.97 }}
        transition={springs.gentle}
        style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 480, zIndex: 101,
        }}
      >
        <Glass depth={3} style={{ padding: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Import Meeting — {founder.name}</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
              <X size={16} color={T.textMuted} />
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input style={inputStyle} type="date" value={meetingDate} onChange={e => setMeetingDate(e.target.value)} />
            <input style={inputStyle} placeholder="Fireflies URL (optional)" value={transcriptUrl} onChange={e => setTranscriptUrl(e.target.value)} />
            <textarea
              style={{ ...inputStyle, minHeight: 120, resize: 'vertical' }}
              placeholder="Paste transcript text (optional)"
              value={transcriptText}
              onChange={e => setTranscriptText(e.target.value)}
            />
            <div style={{ fontSize: 11, color: T.textMuted }}>
              Provide a Fireflies URL, transcript text, or both. The meeting will be processed automatically.
            </div>
            <button onClick={save} disabled={(!transcriptUrl.trim() && !transcriptText.trim()) || saving} style={{
              padding: '10px 20px', borderRadius: 10, border: 'none',
              background: T.blue, color: '#fff', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              opacity: (!transcriptUrl.trim() && !transcriptText.trim()) || saving ? 0.5 : 1, marginTop: 4,
            }}>
              {saving ? 'Importing...' : 'Import & Process'}
            </button>
          </div>
        </Glass>
      </motion.div>
    </>
  );
};

// ─── Founder Detail ──────────────────────────────────────────────────────────
const FounderDetail = ({ founder, meetings, meetingActions, onBack, onUpdate }) => {
  const [showImport, setShowImport] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [processing, setProcessing] = useState(false);

  const founderMeetings = meetings
    .filter(m => m.founder_id === founder.id)
    .sort((a, b) => new Date(b.meeting_date) - new Date(a.meeting_date));

  const pendingCount = founderMeetings.filter(m => m.status === 'pending').length;

  const openActions = meetingActions.filter(a => {
    const meeting = founderMeetings.find(m => m.id === a.meeting_id);
    return meeting && !a.is_done;
  });

  const processPending = async () => {
    if (!isLocalAvailable()) return;
    setProcessing(true);
    try {
      await apiFetch('/meetings/process-pending', { method: 'POST' });
      onUpdate();
    } catch (err) {
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const saveNotes = async (html) => {
    await supabase.from('founders').update({ notes: html }).eq('id', founder.id);
  };

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={springs.gentle}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={onBack} style={{
          background: 'rgba(0,0,0,0.04)', border: 'none', cursor: 'pointer',
          width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ArrowLeft size={16} color={T.textMid} />
        </button>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: T.text, letterSpacing: '-0.02em' }}>{founder.name}</h2>
          <div style={{ display: 'flex', gap: 12, fontSize: 12, color: T.textMuted, marginTop: 2 }}>
            {founder.company_name && <span>{founder.company_name}</span>}
            {founder.email && <span>{founder.email}</span>}
            {founder.cohort && <span>{founder.cohort}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowEdit(true)} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 10, border: 'none',
            background: 'rgba(0,0,0,0.04)', color: T.textMid, cursor: 'pointer',
            fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
          }}>
            <Pencil size={12} /> Edit
          </button>
          {pendingCount > 0 && (
            <button onClick={processPending} disabled={processing} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 10, border: 'none',
              background: T.amberLight, color: T.amber, cursor: 'pointer',
              fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
              opacity: processing ? 0.5 : 1,
            }}>
              <RefreshCw size={12} />
              Process {pendingCount} pending
            </button>
          )}
          <button onClick={() => setShowImport(true)} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 10, border: 'none',
            background: T.blueLight, color: T.blue, cursor: 'pointer',
            fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
          }}>
            <Upload size={12} /> Import Meeting
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, alignItems: 'start' }}>
        {/* Left column: Notes + Meetings */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Notes */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: T.textLight, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
              Notes
            </div>
            <NotesEditor value={founder.notes} onSave={saveNotes} />
          </div>

          {/* Meetings Timeline */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: T.textLight, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>
              Meetings ({founderMeetings.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {founderMeetings.length === 0 ? (
                <div style={{ fontSize: 13, color: T.textMuted, padding: 20, textAlign: 'center' }}>
                  No meetings yet. Import one to get started.
                </div>
              ) : (
                founderMeetings.map(meeting => (
                  <MeetingCard
                    key={meeting.id}
                    meeting={meeting}
                    actions={meetingActions.filter(a => a.meeting_id === meeting.id)}
                    onUpdate={onUpdate}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        {/* Open Actions Sidebar */}
        <div>
          <div style={{ fontSize: 10, fontWeight: 600, color: T.textLight, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>
            Open Actions ({openActions.length})
          </div>
          <Glass depth={0} style={{ padding: 16 }}>
            {openActions.length === 0 ? (
              <div style={{ fontSize: 12, color: T.textMuted, textAlign: 'center', padding: 12 }}>
                No open actions
              </div>
            ) : (
              openActions.map(action => (
                <div key={action.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.04)',
                }}>
                  <CircleDot size={12} color={action.assignee === 'mike' ? T.blue : T.textLight} style={{ flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <div style={{ fontSize: 12, color: T.text }}>{action.description}</div>
                    <div style={{ fontSize: 10, color: action.assignee === 'mike' ? T.blue : T.textLight, marginTop: 2 }}>
                      {action.assignee === 'mike' ? 'You' : 'Founder'}
                    </div>
                  </div>
                </div>
              ))
            )}
          </Glass>
        </div>
      </div>

      <AnimatePresence>
        {showImport && (
          <ImportMeetingDialog
            founder={founder}
            onClose={() => setShowImport(false)}
            onUpdate={onUpdate}
          />
        )}
        {showEdit && (
          <FounderDialog
            founder={founder}
            onClose={() => setShowEdit(false)}
            onUpdate={onUpdate}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─── Mentoring View (Main) ───────────────────────────────────────────────────
export const MentoringView = ({ founders, meetings, meetingActions, onUpdate }) => {
  const [selectedFounder, setSelectedFounder] = useState(null);
  const [showAddFounder, setShowAddFounder] = useState(false);
  const [processingAll, setProcessingAll] = useState(false);

  // Keep selectedFounder in sync with latest data
  const activeFounder = selectedFounder
    ? founders.find(f => f.id === selectedFounder.id) || selectedFounder
    : null;

  const pendingTotal = meetings.filter(m => m.status === 'pending').length;

  const founderStats = useMemo(() => {
    const stats = {};
    founders.forEach(f => {
      const fMeetings = meetings.filter(m => m.founder_id === f.id);
      const fActions = meetingActions.filter(a => fMeetings.some(m => m.id === a.meeting_id));
      stats[f.id] = {
        meetingCount: fMeetings.length,
        openActions: fActions.filter(a => !a.is_done).length,
        lastMeeting: fMeetings.length > 0
          ? fMeetings.reduce((latest, m) => m.meeting_date > latest ? m.meeting_date : latest, fMeetings[0].meeting_date)
          : null,
      };
    });
    return stats;
  }, [founders, meetings, meetingActions]);

  const processAllPending = async () => {
    if (!isLocalAvailable()) return;
    setProcessingAll(true);
    try {
      await apiFetch('/meetings/process-pending', { method: 'POST' });
      onUpdate();
    } catch (err) {
      console.error(err);
    } finally {
      setProcessingAll(false);
    }
  };

  if (activeFounder) {
    return (
      <FounderDetail
        founder={activeFounder}
        meetings={meetings}
        meetingActions={meetingActions}
        onBack={() => setSelectedFounder(null)}
        onUpdate={onUpdate}
      />
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: T.text, letterSpacing: '-0.03em' }}>Mentoring</h1>
          <p style={{ fontSize: 13, color: T.textMuted, marginTop: 4 }}>
            {founders.length} founder{founders.length !== 1 ? 's' : ''}
            {pendingTotal > 0 && (
              <span style={{ color: T.amber, fontWeight: 600 }}> &middot; {pendingTotal} meeting{pendingTotal !== 1 ? 's' : ''} pending</span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {pendingTotal > 0 && (
            <button onClick={processAllPending} disabled={processingAll} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 10, border: 'none',
              background: T.amberLight, color: T.amber, cursor: 'pointer',
              fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
              opacity: processingAll ? 0.5 : 1,
            }}>
              <RefreshCw size={12} /> Process All
            </button>
          )}
          <button onClick={() => setShowAddFounder(true)} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 10, border: 'none',
            background: T.blueLight, color: T.blue, cursor: 'pointer',
            fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
          }}>
            <Plus size={12} /> Add Founder
          </button>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: 12,
      }}>
        {founders.map(founder => (
          <FounderCard
            key={founder.id}
            founder={founder}
            meetingCount={founderStats[founder.id]?.meetingCount || 0}
            openActions={founderStats[founder.id]?.openActions || 0}
            lastMeeting={founderStats[founder.id]?.lastMeeting}
            onClick={() => setSelectedFounder(founder)}
          />
        ))}
      </div>

      <AnimatePresence>
        {showAddFounder && (
          <FounderDialog
            onClose={() => setShowAddFounder(false)}
            onUpdate={onUpdate}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};
